import { StateType } from '@central-products/interface-session-service';
import { publishSticky, subscribe, unsubscribe } from '@wh/message-bus';
import { bogConstants } from './constants';
import { bogOptInMutation, bogStatusQuery } from './queries';

const initialConditions = {
  loggedIn: false,
  status: bogConstants.status.notAvailable,
};

export const bestOddsGuaranteedPlugin = {
  _cleanup: null,
  _conditions: initialConditions,
  _dataAccessService: null,
  _sessionState: null,

  postBootstrap(ctx) {
    if (!bogConstants.enabledLocales.includes(ctx.locale)) {
      publishSticky(bogConstants.messageBus.statusTopic, bogConstants.status.notAvailable);
      return;
    }

    this._dataAccessService = ctx.bootstrapperFeatures['data-access-service'];

    ctx.bootstrapperFeatures['session-service']?.getState().then(({ state }) => this._handleSessionStateChange(state));
    ctx.bootstrapperFeatures['session-service']?.onStateChanged(({ state }) => this._handleSessionStateChange(state));
  },

  async _handleSessionStateChange(state) {
    // Required in case SessionState was updated before onStateChanged is registered.
    // Changes are deduplicated due to possibility of registering the same state twice during application startup.
    if (state === this._sessionState) {
      return;
    } else {
      this._sessionState = state;
    }

    if (state === StateType.SESSION_REQUESTED) {
      return;
    }

    if (state === StateType.SESSION_EXISTS) {
      this._cleanup?.();
      this._cleanup = await this._initBOGHandler();
      return;
    }

    this._patchConditions(initialConditions);
    this._cleanup?.();
    this._cleanup = null;
  },

  async _initBOGHandler() {
    this._patchConditions({
      loggedIn: true,
      status: await this._queryBOGStatus(),
    });

    if (this._conditions.status === bogConstants.status.notAvailable) {
      // If BOG is not available for user it is meaningless to observe other conditions.
      return () => {};
    }

    const subscribeBOGOptInCleanup = this._subscribeBOGOptIn();

    return () => {
      subscribeBOGOptInCleanup?.();
    };
  },

  _patchConditions(conditions) {
    this._conditions = { ...this._conditions, ...conditions };
    this._publishBOGStatus();
  },

  _publishBOGStatus() {
    if (!this._conditions.loggedIn) {
      publishSticky(bogConstants.messageBus.statusTopic, bogConstants.status.notAvailable);
      return;
    }

    publishSticky(bogConstants.messageBus.statusTopic, this._conditions.status);
  },

  _subscribeBOGOptIn() {
    if (this._conditions.status !== bogConstants.status.eligible) {
      // If user is not in eligible state, it is meaningless to allow opting in.
      return () => {};
    }

    let optInMutationInProgress = false;
    const unsubscribeId = subscribe(bogConstants.messageBus.optInTopic, async () => {
      if (optInMutationInProgress) {
        return;
      }

      optInMutationInProgress = true;
      this._mutateBOGOptIn()
        .then((res) => {
          if (res?.result === 'SUCCESS') {
            this._patchConditions({ status: bogConstants.status.optedIn });
            // It is no longer needed to observe this topic, as user is already opted in.
            unsubscribe(unsubscribeId);
          } else {
            throw new Error(res?.error);
          }
        })
        .catch((err) => {
          console.error('Failed to opt in to BOG.', err);
        })
        .finally(() => {
          optInMutationInProgress = false;
        });
    });

    return () => {
      unsubscribe(unsubscribeId);
    };
  },

  _queryBOGStatus() {
    return this._dataAccessService
      .query({
        query: bogStatusQuery,
        fetchPolicy: 'no-cache',
      })
      .then((body) => body?.data?.bogStatus ?? bogConstants.status.notAvailable)
      .catch(() => bogConstants.status.notAvailable);
  },

  _mutateBOGOptIn() {
    return this._dataAccessService
      .mutate({
        mutation: bogOptInMutation,
        fetchPolicy: 'no-cache',
      })
      .then((body) => body?.data?.bogOptIn ?? { result: 'ERROR' })
      .catch((error) => ({ result: 'ERROR', error }));
  },
};
